import { formatAmount, OBSERVATION_LABELS, periodSummary, usableObservation,
  type FinancePeriod, type Statement, type Reconciliation } from './finance-statement-model';

/** Print only the selected observations already held by this authorized view. */
export default function StatementPrint({ statement, periods, unit, liveSource, reconciliations }: {
  statement: Statement; periods: FinancePeriod[]; unit: number;
  liveSource: { label: string; fetchedAt: number; note: string }; reconciliations: Reconciliation[];
}) {
  if (!periods.length) return null;
  const groups: FinancePeriod[][] = [];
  for (let offset = 0; offset < periods.length; offset += 4) groups.push(periods.slice(offset, offset + 4));
  const units = unit === 1 ? 'Actual (whole dollars displayed)' : unit === 1000 ? 'Thousands' : 'Millions';
  const selectedWindow = `${periods[0].label} through ${periods.at(-1)!.label}`;
  return <div className="fs-print-only" aria-label="Filtered statement print layout">
    <h1>{statement.title}</h1>
    <p>Confidential financial report · share only with authorized recipients.</p>
    <p>{statement.entity} · {statement.currency} · {units} · {selectedWindow}</p>
    <p>Source: {liveSource.label}. Fetched (UTC): {new Date(liveSource.fetchedAt * 1000).toISOString()}.</p>
    {periods.some((p) => p.status === 'open') && <p><strong>Open period included — figures may change before close.</strong></p>}
    {periods.some((p) => p.status === 'unknown') && <p><strong>Period close status unknown — do not treat these periods as closed.</strong></p>}
    {groups.map((group, index) => <section key={group[0].id} className="fs-print-sheet">
      <table><caption>{statement.title} · period columns {index * 4 + 1}–{index * 4 + group.length} of {periods.length}</caption>
        <thead><tr><th>Account / line</th>{group.map((p) => <th key={p.id}>{p.label}<small>{p.status}</small></th>)}
          <th>{statement.kind === 'closing' ? 'Selected-window closing balance' : 'Selected-window total'}</th></tr></thead>
        <tbody>{statement.rows.map((row) => {
          const total = periodSummary(row, periods, statement.kind);
          return <tr key={row.id}><th>{row.label}</th>
            {group.map((p) => { const value = usableObservation(row.values[p.id]); return <td key={p.id}>
              {formatAmount(value.amount, unit)}<small>{OBSERVATION_LABELS[value.status]}</small></td>; })}
            <td>{formatAmount(total.amount, unit)}<small>{OBSERVATION_LABELS[total.status]}</small></td>
          </tr>;
        })}</tbody>
      </table>
    </section>)}
    <section className="fs-print-appendix">
      <h2>Basis of preparation</h2>
      <dl>
        <dt>Report</dt><dd>{statement.title}</dd><dt>Entity / population</dt><dd>{statement.entity}</dd>
        <dt>Basis</dt><dd>{statement.basis}</dd><dt>Currency / units</dt><dd>{statement.currency} / {units}</dd>
        <dt>Selected periods</dt><dd>{selectedWindow} ({periods.length} supplied periods)</dd>
        <dt>Source</dt><dd>{liveSource.label}</dd><dt>Source observation time (UTC)</dt><dd>{new Date(liveSource.fetchedAt * 1000).toISOString()}</dd>
      </dl>
      <p>{liveSource.note}</p>
      <p>{statement.kind === 'closing' ? 'Closing balances are not summed; the summary uses the last selected period.' : 'The selected-window total sums the selected periods; missing inputs keep the total unknown.'}</p>
      <p>Display rounding does not alter source values. Parentheses mean negative; values below a display increment use threshold labels. Measured does not mean independently reconciled.</p>
      <p>This is the currently loaded report, not a full financial package or a ledger-detail export. Hidden periods are excluded. A downloaded or printed copy cannot be recalled when access changes.</p>
      <h3>Reconciliation</h3>
      {!reconciliations.length ? <p>No independent checks supplied; reconciliation has not been established.</p>
        : <ul>{reconciliations.map((check) => <li key={check.id}>{check.title}: {check.status}; difference {formatAmount(check.difference, 1)} {statement.currency}; owner {check.owner}. {check.note}</li>)}</ul>}
      <h3>Selected observations — unrounded source values and provenance</h3>
      {statement.rows.map((row) => <section key={row.id}><h4>{row.label}</h4><ul>{periods.map((p) => {
        const value = usableObservation(row.values[p.id]);
        return <li key={p.id}>{p.label} ({p.status}): {value.amount === null ? 'Could not measure' : String(value.amount)} {statement.currency}; {OBSERVATION_LABELS[value.status]}. {value.note}</li>;
      })}</ul></section>)}
    </section>
  </div>;
}
